import React, { useState, useEffect, useMemo } from "react";
import Checkbox from "~/shared/ui/Fields/Checkbox";

import s from "./AccordionPermissions.less";
import Back from "~/components/Icons/svg/arrow-accordion";
import clsx from "clsx";

interface Permission {
  id?: string;
  accessDescriptionId?: string;
  description: string;
  moduleId: string;
}

interface Module {
  id: string;
  description: string;
  permissions: Permission[];
}

interface RoleActive {
  value: boolean;
  permissionDescriptionId: string;
}

interface Role {
  id: string;
  name: string;
  accesses?: RoleActive[];
  permissions?: RoleActive[];
}

interface Props {
  modules: Module[];
  roleActive: Role | null;
  title: string;
  userForm?: boolean;
  onPermissionsChange?: (checkedPermissions: Record<string, boolean>) => void;
}

const AccordionPermissions: React.FC<Props> = ({
  modules,
  title,
  roleActive,
  userForm,
  onPermissionsChange,
}) => {
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [checkedPermissions, setCheckedPermissions] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const initialPermissions: Record<string, boolean> = {};

    modules.forEach((module) => {
      module.permissions.forEach((perm) => {
        initialPermissions[perm.id] = false;
      });
    });

    if (roleActive) {
      const source = roleActive.permissions ?? roleActive.accesses ?? [];
      source.forEach((access) => {
        initialPermissions[access.permissionDescriptionId] = access.value;
      });
    }

    setCheckedPermissions(initialPermissions);
    onPermissionsChange?.(initialPermissions);
  }, [roleActive, modules]);

  const toggleAccordion = (id: string) => {
    setOpenModule((prev) => (prev === id ? null : id));
  };

  const handleCheck = (permissionId: string, checked: boolean) => {
    setCheckedPermissions((prev) => {
      const newPermissions = { ...prev, [permissionId]: checked };
      onPermissionsChange?.(newPermissions);
      return newPermissions;
    });
  };

  const toggleSelectAll = (module: Module, checked: boolean) => {
    setCheckedPermissions((prev) => {
      const updated = { ...prev };

      module.permissions.forEach((perm) => {
        updated[perm.id] = checked;
      });

      onPermissionsChange?.(updated);
      return updated;
    });
  };

  const isModuleFullyChecked = (module: Module) =>
    module.permissions.every((perm) => checkedPermissions[perm.id]);

  return (
    <div
      className={clsx(s.wrapper, {
        [s.wrapperUsers]: !!userForm,
      })}
    >
      {!userForm && <div className={s.title}>{title}</div>}

      {modules.map((module) => {
        const allChecked = isModuleFullyChecked(module);

        return (
          <div key={module.id} className={s.accordion}>
            <div
              className={s.header}
              onClick={() => toggleAccordion(module.id)}
            >
              <span>{module.description}</span>

              <span className={s.arrow}>
                {openModule === module.id ? (
                  <div className={s.top}>
                    <Back />
                  </div>
                ) : (
                  <div className={s.bottom}>
                    <Back />
                  </div>
                )}
              </span>
            </div>

            {openModule === module.id && (
              <div className={s.body}>
                <div className={s.permissionRow}>
                  <Checkbox
                    id={`selectAll-${module.id}`}
                    name={`selectAll-${module.id}`}
                    checked={allChecked}
                    onCheck={(val) => toggleSelectAll(module, val)}
                  />
                  <label
                    htmlFor={`selectAll-${module.id}`}
                    className={clsx(s.permissionLabel, s.bold)}
                  >
                    Выделить всё
                  </label>
                </div>

                {module.permissions.map((perm) => (
                  <div key={perm.id} className={s.permissionRow}>
                    <Checkbox
                      id={perm.id}
                      name={perm.id}
                      checked={checkedPermissions[perm.id] ?? false}
                      onCheck={(val) => handleCheck(perm.id, val)}
                    />
                    <label htmlFor={perm.id} className={s.permissionLabel}>
                      {perm.description}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AccordionPermissions;
